import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnInit,
  QueryList,
  Renderer2,
  ViewChildren,
} from '@angular/core';

import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

import { Subscription } from 'rxjs';

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { TranslateService } from '@ngx-translate/core';

import {
  COMPANY_ID_KEY,
  DynamicPropertyRef,
  DynamicPropertyType,
  FreeLimitType,
  ICustomProperty,
  ICustomPropertyCreateDto,
  ValidateAllFormFields,
} from '@tacliatech/types';

import {
  DynamicPropertyService,
  FreeLimitService,
  SandboxService,
  StorageService,
} from '@web-frontend/shared/services';

import { AmplitudeService } from '@web-frontend/shared/amplitude.service';
import { finalize } from 'rxjs/operators';
import { KeyCustomPropertyValidator } from '@web-frontend/shared/validators/custom-properties';

import { BrazeService } from '@web-frontend/shared/services/braze/braze.service';
import { ToastService } from '@web-frontend/shared/services/toast/toast.service';

@Component({
  selector: 'roma-modal-add',
  templateUrl: './modal-add.component.html',
  styleUrls: ['./modal-add.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ModalAddComponent implements OnInit, AfterViewInit {
  @ViewChildren('inputTest') inputs: QueryList<ElementRef>;

  @Input()
  action: 'ADD' | 'EDIT' = 'ADD';

  @Input()
  type: DynamicPropertyRef;

  @Input()
  idGroup: string;

  @Input()
  options: { nameMaxLength: number } = {
    nameMaxLength: 20,
  };

  @Input()
  title: string;

  get disabled() {
    return this.action == 'EDIT';
  }

  form = this.fb.group({
    type: new FormControl(null, [Validators.required]),
    ref: new FormControl(null, [Validators.required]),
    name: new FormControl(null, [
      Validators.required,
      KeyCustomPropertyValidator.checkBlockedCharacters(),
    ]),
    owner: new FormControl(StorageService.GetItem(COMPANY_ID_KEY), [
      Validators.required,
    ]),
    options: new FormArray([]),
    isRequired: new FormControl(false),
    default: new FormControl(null),
  });

  optionsControl = this.form.get('options') as FormArray;
  _defaultOptions: any[];
  showNewOption = false;

  typeItems = [
    {
      value: DynamicPropertyType.Number,
      label: this.i18n.instant('customProperties.NUMBER'),
    },
    {
      value: DynamicPropertyType.Date,
      label: this.i18n.instant('customProperties.DATE'),
    },
    {
      value: DynamicPropertyType.Select,
      label: this.i18n.instant('customProperties.SELECT'),
    },
    {
      value: DynamicPropertyType.String,
      label: this.i18n.instant('customProperties.STRING'),
    },
    {
      value: DynamicPropertyType.Boolean,
      label: this.i18n.instant('customProperties.BOOLEAN'),
    },
  ];

  viewsOptions = {
    showChooseItemsComponent: false,
  };

  showItemsOptions = [];

  dynamicPropertyTypeRef = DynamicPropertyType;
  isLoading = false;
  private sub$ = new Subscription();
  resize$ = this.sandBoxService.screenBusChannel$;

  flightRequest = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ModalAddComponent>,
    private customPropertyService: DynamicPropertyService,
    private i18n: TranslateService,
    private cdRef: ChangeDetectorRef,
    private amplitudeService: AmplitudeService,

    private brazeService: BrazeService,
    @Inject(MAT_DIALOG_DATA) private data: ICustomProperty,
    private ngZone: NgZone,
    private sandBoxService: SandboxService,
    private renderer: Renderer2,
    private toastService: ToastService,
    private freeLimitService: FreeLimitService
  ) {}

  get controls() {
    return this.form.controls;
  }

  ngOnInit(): void {
    this.resolveRef();
    this.listenFormChanges();
  }

  ngAfterViewInit() {
    this.form.valueChanges.subscribe((res) => {
      //console.log(this.form);
    });
  }

  changeType(evt: { value: DynamicPropertyType }) {
    this.form.patchValue({
      type: evt?.value,
    });

    this.optionsControl.clear();
    this.setDefaultOptions();

    if (evt?.value === DynamicPropertyType.Select) {
      this.addInitialOptionsControl();
    }
  }
  private addInitialOptionsControl() {
    [0, 1].forEach((el) => {
      (this.form.get('options') as FormArray).push(
        new FormGroup({
          [el]: new FormControl('', Validators.required),
        })
      );
    });
  }

  addOption() {
    const amountOptions = this.optionsControl.length;
    const pushNewItem = () => {
      [amountOptions].forEach((el) => {
        (this.form.get('options') as FormArray).push(
          new FormGroup({
            [el]: new FormControl('', Validators.required),
          })
        );
      });
    };

    setTimeout(() => {
      this.inputs?.last?.nativeElement?.focus();
    }, 1000);

    if (amountOptions) {
      pushNewItem();
    } else {
      this.addInitialOptionsControl();
    }
  }

  removeOption(index: number) {
    (this.form.get('options') as FormArray).removeAt(index);
  }

  keyDownInputName(evt: KeyboardEvent) {
    const blocked = ['.', ','];

    if (blocked.includes(evt.key)) {
      evt.preventDefault();
    }
  }

  delete() {
    this.isLoading = true;
    this.sub$.add(
      this.customPropertyService
        .deleteGlobal(this.data._id)
        .pipe(
          finalize(() => {
            this.isLoading = true;
          })
        )
        .subscribe((res) => {
          const eventData = {
            event: 'customField_delete',
          };
          this.amplitudeService.sendEvent(eventData);
          const message = `customProperties.deleteCustomFieldMessage`;
          this.toastService.show({
            text: this.i18n.instant(message),
            type: 'success',
            msDuration: 4000,
          });
          this.dialogRef.close('deleted');
        })
    );
  }

  // @ts-ignore
  submit() {
    this.optionsControl.controls.forEach((control) => {
      control.markAllAsTouched();
      control.markAsDirty();
    });
    const isValid = this.checkValidators();

    if (isValid) {
      const value = this.privateTransformFormValues();

      if (this.idGroup === 'newGroup') {
        this.dialogRef.close({ event: 'Close', data: value });
        return true;
      }

      if (this.action === 'EDIT' && this.data) {
        this.isLoading = true;
        this.flightRequest = true;
        this.draw();

        this.sub$.add(
          this.customPropertyService
            .editGlobal({
              ...value,
              _id: this.data._id,
            })
            .pipe(
              finalize(() => {
                this.flightRequest = false;
                this.draw();
              })
            )
            .subscribe(
              (res) => {
                this.isLoading = false;
                this.dialogRef.close({ ...res, ...value });
                const message = `customProperties.successMessage`;
                this.toastService.show({
                  text: this.i18n.instant(message),
                  type: 'success',
                  msDuration: 4000,
                });
              },
              (error) => {
                this.isLoading = false;
                this.toastService.show({
                  text: this.i18n.instant(error?.error?.message),
                  type: 'error',
                  msDuration: 4000,
                });
              }
            )
        );
      } else {
        this.resolveSaveSubmit();
      }
    } else {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
    }
  }

  private resolveSaveSubmit() {
    const eventData = {
      event: 'customField_create',
    };

    const value = this.privateTransformFormValues();

    this.isLoading = true;
    this.flightRequest = true;
    this.draw();

    this.sub$.add(
      this.customPropertyService
        .createGlobal({
          ...value,
        })
        .pipe(
          finalize(() => {
            this.flightRequest = false;
            this.draw();
          })
        )

        .subscribe(
          (res) => {
            this.isLoading = false;
            this.dialogRef.close({ ...res, ...value });
            const message = `customProperties.successMessage`;
            this.toastService.show({
              text: this.i18n.instant(message),
              type: 'success',
              msDuration: 4000,
            });
            this.amplitudeService.sendEvent(eventData);

            // Updates free limit collection
            // this.updateFreeLimit(res);
            this.brazeService.sendEvent(eventData);
          },
          (error) => {
            this.isLoading = false;
            this.toastService.show({
              text: this.i18n.instant(error?.error?.message),
              type: 'error',
              msDuration: 4000,
            });
          }
        )
    );
  }

  cancelEvt() {
    if (!this.isLoading) {
      this.dialogRef.close();
    }
  }

  private privateTransformFormValues(): ICustomPropertyCreateDto {
    const optionsValue = this.optionsControl.value as Array<{
      [key: number]: string;
    }>;

    // Return just value from array Object [key: number]: string;
    // @ts-ignore
    const options = optionsValue.map((element) => {
      for (const key in element) {
        const value = element[key];
        return value;
      }
    });

    return {
      ...this.form.value,
      idGroupCode: this.idGroup ? [this.idGroup] : [],
      options,
    };
  }

  private appendOptionsToForm(options: string[]) {
    const control = this.form.get('options') as FormArray;

    control.clear();

    options.forEach((option, index) => {
      control.push(
        new FormGroup({
          [index]: new FormControl(option),
        })
      );
    });

    //control.setValue(options);
    control.updateValueAndValidity();
  }

  addShowItemsOptions() {
    if (!this.showItemsOptions.length) {
      this.showItemsOptions = [1, 2];
      this.draw();
    }
  }

  private listenFormChanges() {
    this.sub$.add(
      this.form.valueChanges.subscribe(() => {
        this.checkIfShowViewsOptions();
      })
    );
  }

  private checkIfShowViewsOptions() {
    try {
      const res = this.form.value;

      this.viewsOptions = {
        ...this.viewsOptions,
        showChooseItemsComponent: res.type === 'SELECT',
      };

      this.addOrRemoveOptionsValidator(
        res.type === 'SELECT' ? 'ADD' : 'REMOVE'
      );

      this.draw();
    } catch (err) {}
  }

  private addOrRemoveOptionsValidator(options: 'ADD' | 'REMOVE') {
    const control = this.form.get('options');

    if (options === 'ADD') {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private checkValidators() {
    ValidateAllFormFields(this.form);

    return this.form.valid;
  }

  private resolveRef() {
    if (this.data) {
      this.form.patchValue({ ...this.data });
      this.setDefaultOptions();
      this.checkIfShowViewsOptions();

      if (this.data?.options) {
        this.appendOptionsToForm(this.data.options);
      }

      if (this.idGroup !== 'newGroup') {
        this.form.get('type').disable();
      }
    } else {
      this.form.patchValue({
        ref: this.type,
      });
    }
  }

  private draw() {
    this.ngZone.runOutsideAngular(() => {
      this.cdRef.markForCheck();
      this.ngZone.run(() => {
        this.cdRef.detectChanges();
      });
    });
  }

  eventChangeSelect(evt) {
    if (this.form?.get('type')?.value == 'BOOLEAN') {
      this.form.patchValue({
        default: evt,
      });
    }
  }

  setDefaultOptions() {
    if (this.form?.get('type')?.value == 'BOOLEAN') {
      this.form.patchValue({
        default: this.i18n.instant('general.no'),
      });
      this._defaultOptions = [
        this.i18n.instant('general.no'),
        this.i18n.instant('general.yes'),
      ];
    }
  }

  updateFreeLimit(customProperty, onInsert = true) {
    if ('_id' in customProperty) {
      this.sub$.add(
        this.freeLimitService
          .updateLimit(FreeLimitType.AllowedTypes.CustomProperty, {
            onInsert,
            customProperty: customProperty['_id'],
          })
          .subscribe((freeLimit) => {
            this.freeLimitService.setFreeLimit(freeLimit);

            // this.draw();
          })
      );
    }
  }

  changeCheckBox() {
    this.form.get('isRequired').setValue(!this.form.get('isRequired').value);
  }
}
